import { type AirdropFarmingExchangeId, type PersistedAirdropFarmingExchangeId } from "@cede/types";

/**
 * Base type that all airdrop farming exchange configs must satisfy.
 * Enforces that every exchange declares its builder fee.
 */
export type ExchangeConfigBase = {
  /** Exchange maker fee in basis points (e.g. 1 = 1 bps = 0.01%). */
  MAKER_FEE_BPS: number;
  /**
   * Builder fee in basis points (e.g. 0.5 = 0.5 bps = 0.005%). 0 if no builder
   * fee. Some exchanges (e.g. Decibel) require an integer u64 on-chain — see
   * the per-exchange config type for unit constraints.
   */
  BUILDER_FEE_BPS: number;
  /**
   * True when reducing orders against an existing position consume no fresh
   * margin (net-based risk model). Used by airdrop-farming asymmetric grid
   * sizing (DEV-522) to safely oversize the reducing side: on net-margin
   * venues, a sell against a long doesn't lock new margin so the boost is
   * free up to the position size. On gross-margin venues the boost is
   * clamped to the actual remaining headroom.
   *
   * Conservative default: only set true once a venue is verified by a
   * trapped-state test order. Hibachi confirmed; others gated per audit.
   */
  NET_MARGIN: boolean;
};

type HyperliquidConfig = ExchangeConfigBase & {
  BUILDER_ADDRESS: `0x${string}`;
};

type GrvtConfig = ExchangeConfigBase & {
  BUILDER_ADDRESS: `0x${string}`;
  /** Max builder fee in basis points — user trust limit for EIP-712 approval */
  BUILDER_MAX_FEE_BPS: number;
};

/**
 * Hotstuff uses a dedicated "broker" wallet (terminology differs from
 * Hyperliquid's "builder" but the semantics are identical: user signs an
 * `approveBrokerFee` EIP-712 action authorizing this address up to
 * `BUILDER_MAX_FEE_BPS`, and every `placeOrder` carries a top-level
 * `brokerConfig: { broker, fee }` so the exchange credits the fee here).
 * Single broker wallet across all environments — Hotstuff's broker registry
 * is global, so dev / staging / prod all attribute to the same address.
 */
type HotstuffConfig = ExchangeConfigBase & {
  BUILDER_ADDRESS: `0x${string}`;
  /** Max broker fee in basis points — user trust limit for EIP-712 approval */
  BUILDER_MAX_FEE_BPS: number;
};

type ExtendedConfig = ExchangeConfigBase & {
  API_BASE_URL: string;
  BUILDER_ID: number;
};

type ZeroOneConfig = ExchangeConfigBase & {
  WEB_SERVER_URL: string;
  APP_ID: string;
};

/**
 * Decibel uses an integer `u64` for `builder_fee` on-chain (per
 * `approve_max_builder_fee_for_subaccount(subaccount, builder, fee_bps: u64)`),
 * so `BUILDER_FEE_BPS` here MUST be a positive integer — the minimum non-zero
 * unit is 1 bps (= 0.01%). Decimal values like 0.5 are not representable;
 * `safeInteger` in the CCXT layer would floor them to 0 and the on-chain Move
 * arg would reject the float. Decibel docs require an Aptos-native subaccount
 * address (NOT EVM-derived) for `BUILDER_ADDRESS`, padded to 64 hex chars.
 */
type DecibelConfig = ExchangeConfigBase & {
  BUILDER_ADDRESS: `0x${string}`;
};

/**
 * Per-exchange config for airdrop farming.
 *
 * Uses the `satisfies` pattern (rather than a generic helper) so that each
 * entry preserves its literal type (HyperliquidConfig, GrvtConfig, etc.) —
 * widening through a `Record<K, ExchangeConfigBase>` parameter would erase
 * the extra fields like `BUILDER_ADDRESS`, `API_BASE_URL`, etc.
 *
 * The outer `satisfies Record<AirdropFarmingExchangeId, ExchangeConfigBase>`
 * still enforces exhaustiveness: missing an exchange → compile error.
 */
export const AIRDROP_FARMING_EXCHANGE_CONFIGS = {
  // Perps DEX — Hyperliquid & HIP-3 platforms (all share the same builder address and fee)
  // HIP-3 growth maker fee: 0.3 bps (source: https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees)
  hyperliquid: {
    MAKER_FEE_BPS: 0.3,
    BUILDER_FEE_BPS: 0.5,
    BUILDER_ADDRESS: "0x4E173918Ac76acD534178ceFc33d169CAf344A2A",
    NET_MARGIN: true,
  } satisfies HyperliquidConfig,
  tradexyz: { MAKER_FEE_BPS: 0.3, BUILDER_FEE_BPS: 0.5, NET_MARGIN: true } satisfies ExchangeConfigBase,
  hyna: { MAKER_FEE_BPS: 0.3, BUILDER_FEE_BPS: 0.5, NET_MARGIN: true } satisfies ExchangeConfigBase,
  cash: { MAKER_FEE_BPS: 0.3, BUILDER_FEE_BPS: 0.5, NET_MARGIN: true } satisfies ExchangeConfigBase,

  // Perps DEX — standalone
  // GRVT maker fee: -0.01 bps (rebate) — source: https://help.grvt.io/en/articles/9614699
  grvt: {
    MAKER_FEE_BPS: -0.01,
    BUILDER_FEE_BPS: 0.5,
    BUILDER_ADDRESS: "0x4E173918Ac76acD534178ceFc33d169CAf344A2A",
    BUILDER_MAX_FEE_BPS: 2,
    NET_MARGIN: true,
  } satisfies GrvtConfig,

  // Extended maker fee: 0 bps — source: https://docs.extended.exchange
  extended: {
    MAKER_FEE_BPS: 0,
    BUILDER_FEE_BPS: 0.5,
    API_BASE_URL: "https://api.starknet.extended.exchange",
    BUILDER_ID: 239783,
    NET_MARGIN: true,
  } satisfies ExtendedConfig,

  // Paradex Pro trader (API) maker fee: 0.3 bps — source: https://docs.paradex.trade/trading/trading-fees
  paradex: { MAKER_FEE_BPS: 0.3, BUILDER_FEE_BPS: 0, NET_MARGIN: true } satisfies ExchangeConfigBase,

  // ZeroOne maker fee: 1 bps — source: https://docs.01.xyz
  zeroone: {
    MAKER_FEE_BPS: 1,
    BUILDER_FEE_BPS: 0,
    WEB_SERVER_URL: "https://zo-mainnet.n1.xyz",
    APP_ID: "zoau54n5U24GHNKqyoziVaVxgsiQYnPMx33fKmLLCT5",
    NET_MARGIN: false,
  } satisfies ZeroOneConfig,

  // Hibachi maker fee: 0 bps — source: https://docs.hibachi.xyz/hibachi-docs/trading/fees
  // NET_MARGIN: confirmed via trapped-state test order (DEV-522 ticket).
  hibachi: { MAKER_FEE_BPS: 0, BUILDER_FEE_BPS: 0, NET_MARGIN: true } satisfies ExchangeConfigBase,

  // Hotstuff maker fee: 0 bps. 0.5 bps broker fee, user-approved at the same
  // cap (no headroom) via the `approveBrokerFee` EIP-712 action. Broker
  // wallet is the same Cede Labs address used across other exchanges; one
  // global value because Hotstuff's broker registry is shared across envs.
  // NET_MARGIN: confirmed via DEV-522 SDK probe — an oversized reduce-only
  // LIMIT (notional > headroom × 2) was accepted on the book, proving the
  // exchange does not lock fresh margin on reducing orders.
  hotstuff: {
    MAKER_FEE_BPS: 0,
    BUILDER_FEE_BPS: 0.5,
    BUILDER_MAX_FEE_BPS: 2,
    BUILDER_ADDRESS: "0x4E173918Ac76acD534178ceFc33d169CAf344A2A",
    NET_MARGIN: true,
  } satisfies HotstuffConfig,

  // CEX Perps
  bitget: { MAKER_FEE_BPS: 1, BUILDER_FEE_BPS: 0, NET_MARGIN: false } satisfies ExchangeConfigBase,

  // Decibel maker fee: 1.1 bps — source: https://docs.decibel.trade/for-traders/fees
  decibel: {
    MAKER_FEE_BPS: 1.1,
    BUILDER_FEE_BPS: 0.5,
    BUILDER_ADDRESS: "0xf375ba6776dd44960e460d58e3f5d0ca645bf5d27210a3f16c6adc6abae78c03",
    NET_MARGIN: true,
  } satisfies DecibelConfig,

  // Rise maker fee: 1 bps. No builder/broker fee surface yet.
  //
  // NET_MARGIN: relies on the live observation that the airdrop-farming bot
  // can place reduce-only orders against open positions without margin
  // rejection — Rise's cross-margin model nets reducing orders against the
  // existing position rather than charging fresh isolated margin. The
  // DEV-522 SDK probe (`_rise_net_margin_audit.mjs`) needs to be re-run
  // with notional STRICTLY greater than the available margin headroom to
  // produce a hard proof (the 2026-05-27 run had notional < headroom, which
  // accepts under either margin model). Until that hard proof lands, treat
  // this flag as empirically validated, not audit-proven.
  rise: { MAKER_FEE_BPS: 1, BUILDER_FEE_BPS: 0, NET_MARGIN: true } satisfies ExchangeConfigBase,

  // CEX Spot — sells settle against existing inventory and don't consume fresh margin.
  mexc: { MAKER_FEE_BPS: 10, BUILDER_FEE_BPS: 0, NET_MARGIN: false } satisfies ExchangeConfigBase,
  kucoin: { MAKER_FEE_BPS: 10, BUILDER_FEE_BPS: 0, NET_MARGIN: false } satisfies ExchangeConfigBase,
  cryptocom: { MAKER_FEE_BPS: 40, BUILDER_FEE_BPS: 0, NET_MARGIN: false } satisfies ExchangeConfigBase,
  bybit: { MAKER_FEE_BPS: 10, BUILDER_FEE_BPS: 0, NET_MARGIN: false } satisfies ExchangeConfigBase,
};

/**
 * Compile-time exhaustiveness check — forces a type error if
 * SUPPORTED_AIRDROP_FARMING_EXCHANGES gains a new exchange without a matching
 * entry above. Split from the declaration so the outer literal retains its
 * precise per-exchange types (HyperliquidConfig, GrvtConfig, …) instead of
 * widening to `ExchangeConfigBase`, which would erase fields like
 * `BUILDER_ADDRESS` and `WEB_SERVER_URL`.
 */
const _exhaustivenessCheck: Record<AirdropFarmingExchangeId, ExchangeConfigBase> = AIRDROP_FARMING_EXCHANGE_CONFIGS;
void _exhaustivenessCheck;

/**
 * Returns builder fee BPS for a given exchange, or 0 if not configured.
 */
export function getBuilderFeeBps(exchangeId: PersistedAirdropFarmingExchangeId): number {
  return AIRDROP_FARMING_EXCHANGE_CONFIGS[exchangeId]?.BUILDER_FEE_BPS ?? 0;
}

/**
 * Returns total maker fee BPS (exchange + builder) for a given exchange.
 */
export function getTotalMakerFeeBps(exchangeId: AirdropFarmingExchangeId): number {
  const config = AIRDROP_FARMING_EXCHANGE_CONFIGS[exchangeId];
  if (!config) return 0;
  return config.MAKER_FEE_BPS + config.BUILDER_FEE_BPS;
}

/**
 * Returns true when the exchange uses net-margin risk semantics — i.e. an
 * order that reduces an existing position consumes no fresh margin. Used by
 * airdrop-farming asymmetric grid sizing (DEV-522) to decide whether the
 * reducing-side boost can safely exceed remaining headroom.
 */
export function isNetMargin(exchangeId: AirdropFarmingExchangeId): boolean {
  return AIRDROP_FARMING_EXCHANGE_CONFIGS[exchangeId]?.NET_MARGIN ?? false;
}
